import { eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback } from 'react';
import { Step } from '~/constants/common/AccountModal';
import { useSendMessageToNative } from '~/context/common/NativeContext';
import type {
  OpenAccountModalPayload,
  StepConfiguration
} from '~/contracts/common';

declare global {
  interface Window {
    carvana?: {
      onLoginSuccess?: () => void;
    };
  }
}

type OpenAccountModalParams<Event> = StepConfiguration & {
  callerIdentifier?: string;
  nativeOptions?: {
    eventPayload?: Record<string, any>;
    eventDispatch: Event;
  };
  onSuccess?: () => Promise<void> | void;
  step?: Step;
};

export function useOpenAccountModal() {
  const { emit } = useCoreEvents();
  const sendMessageToNative = useSendMessageToNative();

  return useCallback(
    <Event>({
      callerIdentifier,
      displayBtnText,
      displayHeading,
      displayTitle,
      nativeOptions,
      onSuccess,
      step
    }: OpenAccountModalParams<Event>) => {
      const stepConfiguration: StepConfiguration = {
        displayTitle,
        displayHeading,
        displayBtnText
      };

      const payload: OpenAccountModalPayload = {
        CallerIdentifier: callerIdentifier,
        OnAuthenticatedSuccess: onSuccess,
        ...(step === Step.CreateAccount
          ? {
              step: Step.CreateAccount,
              CreateAccount: stepConfiguration
            }
          : {
              step: Step.EnterEmail,
              EnterEmail: stepConfiguration
            })
      };

      emit(eventNames.accountModal.open, payload);

      if (nativeOptions) {
        const { eventPayload = {}, eventDispatch } = nativeOptions;

        sendMessageToNative(eventDispatch, eventPayload, {
          callback: () => {
            // Store the onSuccess handler in a global variable, so it can be called from the native app.
            window.carvana = window.carvana ?? {};
            window.carvana.onLoginSuccess = onSuccess;
          }
        });
      }
    },
    [emit, sendMessageToNative]
  );
}

export function useOpenedAccountModal() {
  const { emit } = useCoreEvents();

  return useCallback(() => {
    emit(eventNames.accountModal.opened);
  }, [emit]);
}

export function useCloseAccountModal() {
  const { emit } = useCoreEvents();

  return useCallback(() => {
    emit(eventNames.accountModal.close);
  }, [emit]);
}

export function useClosedAccountModal() {
  const { emit } = useCoreEvents();

  return useCallback(
    (method: string) => {
      emit(eventNames.accountModal.closed, { closedMethod: method });
    },
    [emit]
  );
}
